import { Box, Typography } from '@mui/material';

export const CHART_COLORS = ['#eaa861', '#5236AB', '#EA6361', '#4A90E2', '#C7BDE8'];
export const CATEGORY_KEYS = ['Missing_Type7', 'Actual', 'Missing_Type9', 'Uncertain', 'PendingMissing'];
export const LABEL_MAP = {
  Missing_Type7: 'Missing (Infill)',
  Actual: 'Actual',
  Missing_Type9: 'Missing (Provided)',
  Uncertain: 'Uncertain',
  PendingMissing: 'Pending',
};
export const EMPTY_PIE = CATEGORY_KEYS.map((k) => ({ name: k, value: 0 }));
export const InlineLegend = ({ entries = [], styles }) => (
  <Box sx={styles?.inlineLegend || { display: 'flex', gap: 1 }}>
    {entries.map((e, i) => (
      <Box key={i} sx={styles?.inlineLegendItem || { display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ ...(styles?.inlineLegendSwatch || { width: 12, height: 12, borderRadius: 2 }), backgroundColor: e.color }} />
        <Typography variant="caption" sx={styles?.inlineLegendText || {}}>{e.label}</Typography>
      </Box>
    ))}
  </Box>
);

const monthFmt = new Intl.DateTimeFormat('en-GB', { month: 'short' });
const yearFmt = new Intl.DateTimeFormat('en-GB', { year: 'numeric' });
export const dayFmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric' });

export const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const pctOfTotal = (v, total) => (total ? (safeNumber(v) / total) * 100 : 0);

export function parseValidDates(data, dateKey = 'ReadingDate') {
  if (!Array.isArray(data)) return [];
  return data
    .map((d) => (d?.[dateKey] ? new Date(d[dateKey]) : null))
    .filter((d) => d && !isNaN(d));
}

export function formatMonthYearLabel(dates = []) {
  if (!dates?.length) return '';
  const first = dates[0];
  const last = dates[dates.length - 1];
  const sameMonth = first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear();
  if (sameMonth) return `${monthFmt.format(first)} ${yearFmt.format(first)}`;
  const sameYear = first.getFullYear() === last.getFullYear();
  if (sameYear) return `${monthFmt.format(first)}–${monthFmt.format(last)} ${yearFmt.format(first)}`;
  return `${monthFmt.format(first)} ${yearFmt.format(first)} – ${monthFmt.format(last)} ${yearFmt.format(last)}`;
}

export function fallbackRangeLabel(range) {
  if (!range) return '';
  const toLbl = (s) => (s ? new Date(s).toLocaleDateString('en-GB') : '');
  if (range.start && range.end) return `${toLbl(range.start)} – ${toLbl(range.end)}`;
  if (range.start) return `From ${toLbl(range.start)}`;
  if (range.end) return `To ${toLbl(range.end)}`;
  return '';
}

export function buildDateRangeArray(startIso, endIso) {
  const out = [];
  const s = new Date(startIso);
  const e = new Date(endIso);
  if (isNaN(s) || isNaN(e) || s > e) return out;
  const cur = new Date(s);
  while (cur <= e) {
    out.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export function allocateRoundedPercentages(decimalsByKey) {
  const entries = Object.entries(decimalsByKey || {});
  if (!entries.length) return {};
  const floored = {};
  let sumFloor = 0;
  const fracs = [];
  for (const [k, v] of entries) {
    const num = Number(v) || 0;
    const f = Math.floor(num);
    floored[k] = f;
    sumFloor += f;
    fracs.push({ key: k, frac: num - f, orig: num });
  }
  let remainder = 100 - sumFloor;
  if (remainder < 0) {
    fracs.sort((a, b) => a.frac - b.frac || a.orig - b.orig);
    let idx = 0;
    while (remainder < 0 && idx < fracs.length) {
      floored[fracs[idx].key] = Math.max(0, floored[fracs[idx].key] - 1);
      remainder += 1;
      idx += 1;
    }
    if (remainder < 0) {
      const max = fracs.reduce((a, b) => (a.orig >= b.orig ? a : b));
      Object.keys(floored).forEach(k => floored[k] = 0);
      floored[max.key] = 100;
      remainder = 0;
    }
  }
  if (remainder > 0) {
    fracs.sort((a, b) => b.frac - a.frac || b.orig - a.orig);
    let i = 0;
    while (remainder > 0) {
      const key = fracs[i % fracs.length].key;
      floored[key] = (floored[key] || 0) + 1;
      remainder -= 1;
      i += 1;
    }
  }
  return floored;
}

export function buildPercentBreakdownForRow(valuesByKey, keys = CATEGORY_KEYS) {
  const total = keys.reduce((s, k) => s + safeNumber(valuesByKey[k]), 0);
  const decimalsByKey = Object.fromEntries(keys.map((k) => [k, pctOfTotal(valuesByKey[k], total)]));
  if (total === 0) return { decimalsByKey, displayByKey: undefined, total, hasData: false };
  const displayByKey = allocateRoundedPercentages(decimalsByKey);
  return { decimalsByKey, displayByKey, total, hasData: true };
}

export function toIsoDateUTC(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  return new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate())).toISOString().slice(0, 10);
}

export function makeDefaultRange14() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - 13);
  return { start: toIsoDateUTC(start), end: toIsoDateUTC(end) };
}

export function extractDayAndFullDate(dateStr) {
  if (!dateStr) return { dayLabel: '', fullDate: '' };
  const dateObj = new Date(dateStr);
  if (isNaN(dateObj)) return { dayLabel: '', fullDate: '' };
  return {
    dayLabel: dayFmt.format(dateObj),
    fullDate: dateObj.toLocaleDateString('en-GB'),
  };
}

export function tooltipLabelFormatter(_, payload) {
  const p = payload && payload[0] && payload[0].payload;
  return p?.fullDate || '';
}
